import React, { Fragment } from "react";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { makeStyles } from '@mui/styles';
import { Typography, CircularProgress, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Slide from '@mui/material/Slide';
import classNames from "classnames";
import styles from "./styles";
import compose from "recompose/compose";
import { withStyles } from '@mui/styles'
import PropertyImageSliderDialog from "./PropertyImageSliderDialog";
import { MailProCloseButton } from "./MailProHTMLShortMethods";
import { LOG_MODULES, LOG_CODE_PREFIX, trackException } from "common/errorTracker";
import Base from "common/constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PropertyUrlViewDialog = props => {
  const { classes, hideTrigger, externalOpen, onExternalClose } = props;
  const [open, setOpen] = React.useState(false);
  const [openImageSlider, setImageSlider] = React.useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [contentHtml, setContentHtml] = React.useState('');

  // Optional external control (Mini Table use case): when `externalOpen` is
  // passed, it drives the dialog instead of internal state, and `hideTrigger`
  // suppresses the built-in "View Property Details" trigger div below. Both
  // are no-ops when omitted, so existing callers (PhotoView.jsx) are unaffected.
  const isControlled = externalOpen !== undefined;
  const effectiveOpen = isControlled ? externalOpen : open;

  const fetchContent = () => {
    setIsLoading(true);
    setContentHtml('');

    const homeOwnerURLID = props.property?.homeOwnerURLID;
    const homeownerUrl = homeOwnerURLID
      ? `${Base.APP_URLS.API_BASE}/homeOwnerInfo/${homeOwnerURLID}`
      : props.url;

    if (homeownerUrl) {
      fetch(homeownerUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.text();
        })
        .then(html => {
          setContentHtml(html);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching content:', error);
          trackException({
            module: LOG_MODULES.MAILPRO,
            code: `${LOG_CODE_PREFIX.MAILPRO}HOMEOWNER_URL_LOAD_FAILED`,
            error,
            data: { homeOwnerURLID: props.property?.homeOwnerURLID },
          });
          setIsLoading(false);
          setContentHtml('<p>We are unable to fetch property details at this moment. Please try again later.</p>');
        });
    } else {
      setIsLoading(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    fetchContent();
  };

  // Controlled mode: fetch content when the parent flips externalOpen to true.
  const prevExternalOpenRef = React.useRef(externalOpen);
  React.useEffect(() => {
    if (isControlled && externalOpen && !prevExternalOpenRef.current) {
      fetchContent();
    }
    prevExternalOpenRef.current = externalOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  const handleClose = () => {
    if (isControlled) {
      onExternalClose?.();
    } else {
      setOpen(false);
    }
    setIsLoading(true);
    setContentHtml('');
  };

  const closeImageSlider = (index = 0) => {
    setSelectedImageIndex(index);
    setImageSlider(false);
  };

  const {
    property,
    propertyDetails,
    mailingDetails,
    propertySequenceNumber,
    url
  } = props;

  // Extract homeOwnerURLID from property and construct URL
  const homeOwnerURLID = property?.homeOwnerURLID;
  const homeownerUrl = homeOwnerURLID 
    ? `${Base.APP_URLS.API_BASE}/homeOwnerInfo/${homeOwnerURLID}`
    : url;

  const handleDialogBodyClick = () => {
    if (!openImageSlider) {
      setImageSlider(true);
    }
  };

  return (
    <Fragment>
      {!hideTrigger && (
        <div
          className="tableTdFlex-Second pointer"
          onClick={event => handleClickOpen()}
        >
          <p className="blue-text mailProLinkClass">
            View
            <br />
            Property Details
          </p>
        </div>
      )}

      {effectiveOpen ? (
        <Dialog
          open={true}
          onClose={handleClose}
          scroll={"paper"}
          aria-labelledby="property-url-view-dialog"
          aria-describedby="property-url-view-dialog-description"
          maxWidth={false}
          TransitionComponent={Transition}
          keepMounted
          PaperProps={{
            style: {
              width: '1050px',
              maxWidth: '1050px',
              margin: 0
            }
          }}
        >
          <DialogTitle id="scroll-dialog-title">
            <div className={classes.propertyFactHeader}>
              <Typography variant="h4">Property Facts</Typography>
              <div>
              </div>
              <MailProCloseButton onClick={handleClose} />
            </div>
          </DialogTitle>
          <DialogContent 
            dividers={true}
            style={{ 
              padding: 0,
              margin: 0,
              width: '100%',
              position: 'relative',
              maxHeight: '70vh',
              overflow: 'hidden'
            }}
          >
            {/* Keep iframe mounted but hidden when slider is open */}
            <div
              style={{
                width: '100%',
                position: 'relative',
                display: openImageSlider ? 'none' : 'block',
                maxHeight: '70vh',
                overflow: 'auto',
                margin: 0,
                padding: 0
              }}
            >
              {homeownerUrl ? (
                <>
                  {isLoading && (
                    <Box
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 2
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <div
                    className="property-url-content-wrapper"
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      position: 'relative',
                      margin: '0 auto',
                      padding: '0 16px',
                      cursor: 'pointer',
                      minHeight: '400px',
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      overflow: 'visible'
                    }}
                    onClick={handleDialogBodyClick}
                  >
                    <style dangerouslySetInnerHTML={{
                      __html: `
                        .property-url-content-wrapper > * {
                          max-width: 100% !important;
                          min-width: auto !important;
                          box-sizing: border-box !important;
                        }
                        .property-url-content-wrapper table,
                        .property-url-content-wrapper div:not(.property-url-content-wrapper),
                        .property-url-content-wrapper section {
                          width: 100% !important;
                          max-width: 100% !important;
                          min-width: auto !important;
                        }
                      `
                    }} />
                    <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                  </div>
                </>
              ) : (
                <Box
                  style={{
                    minHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    padding: '32px 16px',
                    textAlign: 'center',
                  }}
                >
                  <InfoOutlinedIcon style={{ fontSize: '48px', color: '#9ca3af' }} />
                  <Typography variant="body1" style={{ fontWeight: 600, color: '#374151' }}>
                    We are unable to fetch property details at this moment.
                  </Typography>
                  <Typography variant="body2" style={{ color: '#6b7280', maxWidth: '360px' }}>
                    Please try again later.
                  </Typography>
                </Box>
              )}
            </div>
            
            {/* Photo slider - shown when openImageSlider is true */}
            {openImageSlider && (
              <PropertyImageSliderDialog
                src={null}
                isOpen={true}
                propertyDetails={propertyDetails}
                mailingDetails={mailingDetails}
                selectedItem={selectedImageIndex}
                closeFn={closeImageSlider}
                propertySequenceNumber={propertySequenceNumber}
                property={property}
              />
            )}
          </DialogContent>
        </Dialog>
      ) : (
        ""
      )}
    </Fragment>
  );
};

export default compose(withStyles(styles))(PropertyUrlViewDialog);

// import React, { Fragment } from "react";
// import Dialog from '@mui/material/Dialog';
// import DialogContent from '@mui/material/DialogContent';
// import DialogTitle from '@mui/material/DialogTitle';
// import { makeStyles } from '@mui/styles';
// import { Typography, CircularProgress, Box } from '@mui/material';
// import Slide from '@mui/material/Slide';
// import classNames from "classnames";
// import styles from "./styles";
// import compose from "recompose/compose";
// import { withStyles } from '@mui/styles'
// import PropertyImageSliderDialog from "./PropertyImageSliderDialog";
// import { MailProCloseButton } from "./MailProHTMLShortMethods";

// const Transition = React.forwardRef(function Transition(props, ref) {
//   return <Slide direction="up" ref={ref} {...props} />;
// });

// const PropertyUrlViewDialog = props => {
//   const [open, setOpen] = React.useState(false);
//   const [openImageSlider, setImageSlider] = React.useState(false);
//   const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
//   const [isLoading, setIsLoading] = React.useState(true);
//   const { classes } = props;
  
//   const handleClickOpen = () => {
//     setOpen(true);
//     setIsLoading(true);
//   };

//   const handleIframeLoad = () => {
//     setIsLoading(false);
//   };

//   const handleClose = () => {
//     setOpen(false);
//     setIsLoading(true);
//   };

//   const closeImageSlider = (index = 0) => {
//     setSelectedImageIndex(index);
//     setImageSlider(false);
//   };

//   const {
//     property,
//     propertyDetails,
//     mailingDetails,
//     propertySequenceNumber,
//     url
//   } = props;

//   // Extract homeOwnerURLID from property and construct URL
//   const homeOwnerURLID = property?.homeOwnerURLID;
//   const homeownerUrl = homeOwnerURLID 
//     ? `${Base.APP_URLS.API_BASE}/homeOwnerInfo/${homeOwnerURLID}`
//     : url;

//   const handleDialogBodyClick = () => {
//     if (!openImageSlider) {
//       setImageSlider(true);
//     }
//   };

//   return (
//     <Fragment>
//       <div
//         className="tableTdFlex-Second pointer"
//         onClick={event => handleClickOpen()}
//       >
//         <p className="blue-text mailProLinkClass">
//           View
//           <br />
//           Property Details
//         </p>
//       </div>

//       {open ? (
//         <Dialog
//           open={true}
//           onClose={handleClose}
//           scroll={"paper"}
//           aria-labelledby="property-url-view-dialog"
//           aria-describedby="property-url-view-dialog-description"
//           maxWidth={false}
//           TransitionComponent={Transition}
//           keepMounted
//           PaperProps={{
//             style: {
//               width: '1050px',
//               maxWidth: '1050px',
//               margin: 0
//             }
//           }}
//         >
//           <DialogTitle id="scroll-dialog-title">
//             <div className={classes.propertyFactHeader}>
//               <Typography variant="h4">Property Facts</Typography>
//               <div>
//               </div>
//               <MailProCloseButton onClick={handleClose} />
//             </div>
//           </DialogTitle>
//           <DialogContent 
//             dividers={true}
//             style={{ 
//               padding: 0,
//               margin: 0,
//               width: '100%',
//               position: 'relative',
//               maxHeight: '70vh',
//               overflow: 'hidden'
//             }}
//           >
//             {/* Keep iframe mounted but hidden when slider is open */}
//             <div
//               style={{
//                 width: '100%',
//                 position: 'relative',
//                 display: openImageSlider ? 'none' : 'block',
//                 maxHeight: '70vh',
//                 overflow: 'auto',
//                 margin: 0,
//                 padding: 0
//               }}
//             >
//               {homeownerUrl ? (
//                 <>
//                   {isLoading && (
//                     <Box
//                       style={{
//                         position: 'absolute',
//                         top: 0,
//                         left: 0,
//                         right: 0,
//                         bottom: 0,
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center',
//                         backgroundColor: 'rgba(255, 255, 255, 0.8)',
//                         zIndex: 2
//                       }}
//                     >
//                       <CircularProgress />
//                     </Box>
//                   )}
//                   <div
//                     style={{
//                       width: '100%',
//                       height: '4500px',
//                       position: 'relative',
//                       overflow: 'hidden',
//                       margin: 0,
//                       padding: 0
//                     }}
//                   >
//                     <iframe
//                       src={homeownerUrl}
//                       style={{
//                         width: '100%',
//                         border: 'none',
//                         height: '100%',
//                         display: 'block',
//                         pointerEvents: 'auto',
//                         overflow: 'hidden',
//                         margin: 0,
//                         padding: 0
//                       }}
//                       scrolling="no"
//                       title="Property URL View"
//                       onLoad={handleIframeLoad}
//                     />
//                     {/* Transparent click overlay - allows scrolling, captures clicks */}
//                     <div
//                       style={{
//                         position: 'absolute',
//                         top: 0,
//                         left: 0,
//                         right: 0,
//                         bottom: 0,
//                         cursor: 'pointer',
//                         zIndex: 1
//                       }}
//                       onMouseDown={(e) => {
//                         const startY = e.clientY;
//                         const startTime = Date.now();
//                         let isScrolling = false;
//                         let mouseMoved = false;
//                         const overlayElement = e.currentTarget;
                        
//                         const handleMouseMove = (moveEvent) => {
//                           mouseMoved = true;
//                           if (Math.abs(moveEvent.clientY - startY) > 5) {
//                             isScrolling = true;
//                             // Temporarily disable overlay to allow scrolling
//                             if (overlayElement && overlayElement.style) {
//                               overlayElement.style.pointerEvents = 'none';
//                               setTimeout(() => {
//                                 if (overlayElement && overlayElement.style) {
//                                   overlayElement.style.pointerEvents = 'auto';
//                                 }
//                               }, 100);
//                             }
//                           }
//                         };
                        
//                         const handleMouseUp = () => {
//                           const timeElapsed = Date.now() - startTime;
//                           // Only trigger click if it was a quick click, not a drag/scroll
//                           if (!mouseMoved && !isScrolling && timeElapsed < 300) {
//                             handleDialogBodyClick();
//                           }
//                           document.removeEventListener('mousemove', handleMouseMove);
//                           document.removeEventListener('mouseup', handleMouseUp);
//                         };
                        
//                         document.addEventListener('mousemove', handleMouseMove);
//                         document.addEventListener('mouseup', handleMouseUp);
//                       }}
//                     />
//                   </div>
//                 </>
//               ) : (
//                 <Typography variant="body1">No URL provided</Typography>
//               )}
//             </div>
            
//             {/* Photo slider - shown when openImageSlider is true */}
//             {openImageSlider && (
//               <PropertyImageSliderDialog
//                 src={null}
//                 isOpen={true}
//                 propertyDetails={propertyDetails}
//                 mailingDetails={mailingDetails}
//                 selectedItem={selectedImageIndex}
//                 closeFn={closeImageSlider}
//                 propertySequenceNumber={propertySequenceNumber}
//                 property={property}
//               />
//             )}
//           </DialogContent>
//         </Dialog>
//       ) : (
//         ""
//       )}
//     </Fragment>
//   );
// };

// export default compose(withStyles(styles))(PropertyUrlViewDialog);